<template>
  <div class="pt-0">
    <ClientOnly>
      <MoneyBackCard />
    </ClientOnly>
    <NuxtLayout name="teasers" v-bind="{ loading }" :no-results="!elements.length">
      <component
        :is="getElementComponent(teaser)"
        v-for="teaser in elements"
        :key="`${getElementKey(teaser)}_${locale}`"
        v-bind="{
          ...teaser,
          ...getElementProps(teaser),
          licensedTerritory,
        }"
        @click="handleElementClick(teaser)"
      />
    </NuxtLayout>
  </div>
</template>

<script lang="ts" setup>
import type {
  RecommendationElement,
  ScratchcardTeaserPayload,
  TeaserPayload,
} from '#api/shared/@types/generated/backend/graphql-schema-types'

import { ScratchcardTeaser, LotteryTeaser, HighFrequencyGameTeaser, WinEverCard } from '#components'
import FreeToPlayTeaser from '#free-to-play/app/components/FreeToPlayTeaser.vue'
import { FREE_TO_PLAY_ITEM_NAME } from '#free-to-play/app/constants/constants'

import HomeTrustTeaserCarousel from '../components/HomeTrustTeaserCarousel.vue'
import { useFetchOptions } from '../utils/fetchOptions'

const { locale } = useI18n()
const licensedTerritory = useLicensedTerritory({ raw: true })

const { loading, result: recommendationElementsResult } = useRecommendationElementsQuery(
  { licensedTerritory },
  useFetchOptions(),
)

const recommendationElements = computed(() => {
  const result = recommendationElementsResult.value?.recommendationElements
  return isGraphqlSuccess(result) ? (result.elements as RecommendationElement[]) : undefined
})

const TRACKING_LIST_NAME = 'Home Lottery Teasers'
const TRACKING_LIST_ID = 'home_lottery_teasers'

const { createTeaserListItems } = useTrackingData()
const { pushSelectListItemEvent, pushViewListEvent } = useTracking()
const { logTrackingError } = useTrackingErrorLog()

const elements = computed(() => recommendationElements.value ?? [])
const trackingTeaserListItems = ref<ReturnType<typeof createTeaserListItems>>([])

const isScratchcardTeaser = (teaser: RecommendationElement): teaser is ScratchcardTeaserPayload =>
  teaser.__typename === 'ScratchcardTeaserPayload'

const getElementKey = (teaser: RecommendationElement) => {
  const identifier = isScratchcardTeaser(teaser) ? teaser.slug : teaser.id
  return `${teaser.__typename}_${identifier}`
}

const getElementComponent = (teaser: RecommendationElement) => {
  switch (teaser.__typename) {
    case 'LotteryTeaserPayload':
      return LotteryTeaser
    case 'ScratchcardTeaserPayload':
      return ScratchcardTeaser
    case 'HighFrequencyTeaserPayload':
      return HighFrequencyGameTeaser
    case 'FreeToPlayTeaserPayload':
      return FreeToPlayTeaser
    case 'WelcomeToWineverElement':
      return WinEverCard
    case 'WinnerTrustElement':
    case 'ReviewElement':
      return HomeTrustTeaserCarousel
    default:
      return null
  }
}

const getElementProps = (teaser: RecommendationElement) => {
  if (teaser.__typename === 'WinnerTrustElement') return { variant: 'winners' as const }
  if (teaser.__typename === 'ReviewElement') return { variant: 'reviews' as const }
  return {}
}

const handleElementClick = (element: RecommendationElement) => {
  const teaserItem = trackingTeaserListItems.value.find((item) => {
    switch (element.__typename) {
      case 'FreeToPlayTeaserPayload':
        return item.item_name === FREE_TO_PLAY_ITEM_NAME
      case 'LotteryTeaserPayload':
        return item.item_id === element.id
      default:
        return false
    }
  })

  if (!teaserItem) return

  try {
    pushSelectListItemEvent({
      items: [teaserItem],
      list_id: TRACKING_LIST_ID,
      list_name: TRACKING_LIST_NAME,
    })
  } catch (err) {
    logTrackingError('select_list_item', err)
  }
}

let unwatchElements: Maybe<() => void> = null
unwatchElements = watchImmediate(elements, (list) => {
  if (!list || list.length === 0) return

  try {
    const filteredTeasers = elements.value.filter(
      (teaser) =>
        teaser.__typename &&
        !['ExploreElement', 'WelcomeToWineverElement'].includes(teaser.__typename),
    ) as TeaserPayload[]
    trackingTeaserListItems.value = createTeaserListItems(filteredTeasers)
    pushViewListEvent({
      items: trackingTeaserListItems.value,
      list_id: TRACKING_LIST_ID,
      list_name: TRACKING_LIST_NAME,
    })

    if (unwatchElements) unwatchElements()
  } catch (err) {
    logTrackingError('view_list', err)
  }
})
</script>
